import { useTranslation } from 'react-i18next';

import { Avatar, Text } from '@/components/ui/display';
import { Alert, Spinner } from '@/components/ui/feedback';
import { Image } from '@/components/ui/media';
import { Modal } from '@/components/ui/overlays';
import { Surface } from '@/components/ui/surfaces';
import type { Organization } from '@/features/organization/types';
import { useImageUrl } from '@/features/organization/useOrganization';

export type PickerFailure = 'refused' | 'failed';

export interface OrganizationPickerDialogProps {
  open: boolean;
  organizations: Organization[];
  pendingId: string | null;
  failure: PickerFailure | null;
  onSelect: (organizationId: string) => void;
}

/** Shows the organizations the user can open. It has no close control. */
export function OrganizationPickerDialog({
  open,
  organizations,
  pendingId,
  failure,
  onSelect,
}: OrganizationPickerDialogProps) {
  const { t } = useTranslation();

  return (
    <Modal open={open} title={t('auth.picker.title')} size="md">
      <div className="flex flex-col gap-3">
        <Text as="p" tone="muted">
          {t('auth.picker.description')}
        </Text>

        {failure && (
          <Alert variant="error">
            {t(failure === 'refused' ? 'auth.picker.noAccess' : 'auth.picker.failed')}
          </Alert>
        )}

        <ul className="flex flex-col gap-2">
          {organizations.map((organization) => (
            <li key={organization.organizationId}>
              <OrganizationRow
                organization={organization}
                pending={pendingId === organization.organizationId}
                disabled={pendingId !== null}
                onSelect={onSelect}
              />
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

interface OrganizationRowProps {
  organization: Organization;
  pending: boolean;
  disabled: boolean;
  onSelect: (organizationId: string) => void;
}

function OrganizationRow({ organization, pending, disabled, onSelect }: OrganizationRowProps) {
  const { t } = useTranslation();
  const logoUrl = useImageUrl(organization.smallLogoObject ?? organization.logoObject);

  return (
    <Surface
      as="button"
      type="button"
      outline="solid"
      radius="sm"
      interactive="quiet"
      disabled={disabled}
      onClick={() => onSelect(organization.organizationId)}
      className="flex w-full items-center gap-3 p-3 text-left disabled:pointer-events-none"
    >
      {logoUrl ? (
        <Image src={logoUrl} fit="contain" radius="xs" className="h-9 w-9 shrink-0" />
      ) : (
        <Avatar name={organization.name} />
      )}

      <span className="flex min-w-0 flex-col">
        <Text variant="title" tone="heading" truncate>
          {organization.name}
        </Text>
        <span className="flex items-center gap-1.5">
          <Swatch color={organization.primaryColor} />
          <Swatch color={organization.secondaryColor} />
        </span>
      </span>

      <Text variant="label" tone="primary" className="ml-auto shrink-0">
        {pending ? <Spinner size="sm" /> : t('auth.picker.select')}
      </Text>
    </Surface>
  );
}

/** Shows one colour as a circle. The colour must be a `#rrggbb` value. */
function Swatch({ color }: { color: string }) {
  if (!/^#[0-9a-fA-F]{6}$/.test(color)) return null;
  return (
    <Surface
      as="span"
      aria-hidden
      outline="solid"
      radius="full"
      style={{ backgroundColor: color }}
      className="mt-1 inline-block h-2.5 w-2.5"
    />
  );
}
